import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/faqs';

export default function About() {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const formActionUrl = 'https://readdy.ai/api/form/d9tgejn8l97n3eas39m0';

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setContactForm({ name: '', email: '', message: '' });
      return;
    }

    if (contactForm.message.length > 500) {
      setFormStatus('error');
      setFormError('留言内容不能超过500个字符');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value.toString());
      });

      const res = await fetch(formActionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      const responseText = await res.text();
      let parsed: any = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore
      }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Brand Introduction */}
          <section className="mb-16">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-6">
              关于性福生活
            </h1>
            <div className="prose max-w-none">
              <p className="text-base text-foreground-700 leading-relaxed mb-4">
                <strong className="text-foreground-900">「性福生活」</strong>是一个专注于亲密关系与性健康知识的内容平台。我们成立于2024年，由一群热爱生活、关注关系质量的专业人士共同创建。
              </p>
              <p className="text-base text-foreground-700 leading-relaxed mb-4">
                我们相信，每一段亲密关系都值得被认真对待。无论是刚刚开始的恋情、步入婚姻多年的伴侣，还是正在探寻自我成长的个人——性福生活都希望成为你值得信赖的知识伙伴。
              </p>
              <p className="text-base text-foreground-700 leading-relaxed">
                我们的内容团队包括注册心理咨询师、三甲医院医师、资深婚姻家庭咨询师等各领域专业人士。所有文章都经过严格的专业审核，确保信息的科学性、准确性和实用性。
              </p>
            </div>
          </section>

          {/* Mission */}
          <section className="mb-16 bg-background-100/50 rounded-lg p-8 md:p-10">
            <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-4">
              我们的使命
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed">
              用专业和温度，帮助每一个中国人建立更健康、更幸福的亲密关系。我们致力于打破传统文化中对性与关系话题的禁忌，用科学、尊重、温暖的方式传播知识，让更多人能够坦然面对自己的情感需求和身体需求。
            </p>
          </section>

          {/* Core Values */}
          <section className="mb-16">
            <h2 className="text-2xl font-heading font-semibold text-foreground-900 mb-8 text-center">
              核心价值观
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-shield-star-line',
                  color: 'primary',
                  title: '专业权威',
                  desc: '每一篇文章都由相关领域的专业人士撰写或审核，确保内容经得起推敲。',
                },
                {
                  icon: 'ri-empathize-line',
                  color: 'accent',
                  title: '温暖共情',
                  desc: '我们理解每段关系的独特性，以尊重和善意对待每一位读者的困惑。',
                },
                {
                  icon: 'ri-lightbulb-flash-line',
                  color: 'secondary',
                  title: '实用有效',
                  desc: '不仅讲道理，更提供可操作的方法和技巧，让知识真正落地到生活中。',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center"
                >
                  <div
                    className={`w-12 h-12 mx-auto mb-4 rounded-full bg-${item.color}-100 flex items-center justify-center`}
                  >
                    <i className={`${item.icon} text-xl text-${item.color}-500`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Testimonials */}
          <section className="mb-16">
            <h2 className="text-2xl font-heading font-semibold text-foreground-900 mb-8 text-center">
              读者心声
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div
                  key={t.name}
                  className="bg-background-50 rounded-lg border border-background-200/70 p-6 flex flex-col"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed flex-1 mb-4">
                    "{t.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-xs font-medium text-primary-600">
                      {t.name.charAt(0)}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section className="mb-16">
            <h2 className="text-2xl font-heading font-semibold text-foreground-900 mb-8 text-center">
              联系我们
            </h2>
            <div className="max-w-lg mx-auto bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
              <form
                onSubmit={handleContactSubmit}
                data-readdy-form="true"
                id="contact-form"
                className="space-y-5"
              >
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入你的姓名"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入你的邮箱地址"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言
                    <span className="text-foreground-400 ml-1">（最多500字）</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入你想说的话..."
                  />
                  <p className="text-xs text-foreground-400 mt-1 text-right">
                    {contactForm.message.length}/500
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full py-3 rounded-lg bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap"
                >
                  {formStatus === 'loading' ? '提交中...' : '发送留言'}
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-secondary-600 text-center">留言发送成功！我们会尽快回复你。</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-500 text-center">{formError}</p>
                )}
              </form>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}